import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ToggleButtonComponent } from './toggle-button.component';
import {
  TOGGLE_BUTTON_ACTIVE_BACKGROUND_CLASSES,
  TOGGLE_BUTTON_DISABLED_ACTIVE_BACKGROUND_CLASSES,
  ToggleButtonSize,
} from './toggle-button.model';

@Component({
  selector: 'fc-toggle-button-group',
  template: `
    <div
      class="relative flex rounded-lg bg-secondary-50 inset-ring inset-ring-secondary-100"
      [class.w-full]="fullWidth()"
    >
      <ng-content />
      @if (activeButton()) {
        <div
          class="absolute inset-y-0 z-0 rounded-lg"
          [class]="activeBackgroundClasses()"
          [class.transition-all]="isInitialized()"
          [class.duration-300]="isInitialized()"
          [style.width]="activeBackgroundWidth()"
          [style.left]="activeBackgroundLeft()"
        ></div>
      }
    </div>
  `,
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleButtonGroupComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  activeIndex = signal<number>(0);
  toggleButtons = contentChildren<ToggleButtonComponent>(ToggleButtonComponent);
  isDisabled = signal<boolean>(false);
  fullWidth = input<boolean>(false);
  size = input<ToggleButtonSize>('md');
  value = model<any>();
  activeButton = computed(() =>
    this.toggleButtons().find((button) => button.selected()),
  );
  activeBackgroundClasses = computed(() => {
    const activeButton = this.activeButton();
    if (activeButton?.isDisabled()) {
      return TOGGLE_BUTTON_DISABLED_ACTIVE_BACKGROUND_CLASSES;
    }
    return TOGGLE_BUTTON_ACTIVE_BACKGROUND_CLASSES[
      activeButton?.activeColor() ?? 'default'
    ];
  });
  activeBackgroundLeft = computed(() => {
    this.width();
    const activeButton = this.activeButton();
    if (!activeButton) return '0';

    return `${activeButton.elRef.nativeElement.offsetLeft}px`;
  });
  ngControl = inject(NgControl, { optional: true, self: true });
  changed = output<any>();
  elRef = inject(ElementRef);
  activeBackgroundWidth = computed(() => {
    this.width();
    const activeButton = this.activeButton();
    if (!activeButton) return '0';

    return `${activeButton.elRef.nativeElement.offsetWidth}px`;
  });

  width = signal(0);
  resizeObserver: ResizeObserver | null = null;
  isInitialized = signal(false);

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    effect(() => {
      const value = this.value();
      const size = this.size();
      const fullWidth = this.fullWidth();
      const isGroupDisabled = this.isDisabled();
      for (const button of this.toggleButtons()) {
        button.selected.set(button.value() === value);
        button.size.set(size);
        button.fullWidth.set(fullWidth);
        button.isGroupDisabled.set(isGroupDisabled);
      }
    });

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.width.set(entry.contentRect.width);
      }
    });
    this.resizeObserver.observe(this.elRef.nativeElement);
  }

  onTouched: any = () => {};
  onChange: any = () => {};

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.isInitialized.set(true));

    this.toggleButtons().map((button, index) => {
      button.onSelect.subscribe(() => {
        this.activeIndex.set(index);
        this.toggleButtons().map((buttonItem) =>
          buttonItem.selected.set(false),
        );
        button.selected.set(true);
        this.onChange(button.value() as any);
        this.changed.emit(button.value() as any);
      });
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  writeValue(value: any): void {
    if (!value) return;
    this.value.set(value);
    this.activeIndex.set(
      this.toggleButtons().findIndex((button) => button.value() === value),
    );
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
